import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, MoreHorizontalIcon, SettingsIcon, XIcon, PenIcon, PlusIcon, TrashIcon, ChevronDownIcon, CheckIcon, AlertTriangleIcon, ArchiveIcon, RefreshCcwIcon, CheckCircleIcon, XCircleIcon, UserPlusIcon, UploadIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../mocks/mockBills';
type TabType = 'due' | 'pending' | 'scheduled' | 'paid' | 'assigned';
interface Threshold {
  id: string;
  approver: string;
  amount: number;
  currency: string;
}
// Cycle day options
const CYCLE_DAY_OPTIONS = [{
  value: 1,
  label: '1st of the month'
}, {
  value: 5,
  label: '5th of the month'
}, {
  value: 10,
  label: '10th of the month'
}, {
  value: 15,
  label: '15th of the month'
}, {
  value: 20,
  label: '20th of the month'
}, {
  value: 25,
  label: '25th of the month'
}, {
  value: -1,
  label: 'Last day of the month'
}];
export function Bills() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('due');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(new Set());
  // Force re-read of mutable mockBills on every render
  const [refreshKey, setRefreshKey] = useState(0);
  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [paymentCycles, setPaymentCycles] = useState<number[]>(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) : [];
  });
  const [numberOfApprovals, setNumberOfApprovals] = useState(0);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  // Modal states
  const [showApprovalsModal, setShowApprovalsModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showCyclesModal, setShowCyclesModal] = useState(false);
  const [tempApprovals, setTempApprovals] = useState(0);
  const [tempThreshold, setTempThreshold] = useState({
    approver: 'Simon Verraest',
    amount: 0,
    currency: ''
  });
  const [tempCycles, setTempCycles] = useState<number[]>([]);
  // Action Modals State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectAction, setRejectAction] = useState<'archive' | 'due'>('archive');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAction, setCancelAction] = useState<'archive' | 'due'>('archive');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [actionTargetId, setActionTargetId] = useState<string | null>(null);
  // Current user for approval logic
  const currentUser = 'Simon Verraest';
  // Whether the current tab supports multi-select
  const showCheckboxes = activeTab !== 'paid';
  // Check if a bill is selectable (for pending tab, only bills the user can approve)
  const isBillSelectable = (bill: (typeof mockBills)[0]) => {
    if (activeTab === 'paid') return false;
    if (activeTab === 'pending') {
      // Only bills assigned to current user or unassigned (open-ended by rules)
      return !bill.approver || bill.approver === currentUser;
    }
    return true;
  };
  // Filter bills based on active tab and search
  const filteredBills = useMemo(() => {
    return mockBills.filter(bill => {
      const matchesSearch = searchQuery === '' || bill.billNumber.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierEmail.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      switch (activeTab) {
        case 'due':
          return bill.status === 'approved' || bill.status === 'overdue';
        case 'pending':
          return bill.status === 'pending_approval' && (!bill.approver || bill.approver === currentUser);
        case 'scheduled':
          return bill.status === 'approved' && bill.approvalStatus === 'approved';
        case 'paid':
          return bill.status === 'paid';
        case 'assigned':
          return bill.approver === 'Simon Verraest' && bill.status === 'pending_approval';
        default:
          return true;
      }
    });
  }, [activeTab, searchQuery, refreshKey]);
  // Refresh data when component mounts (e.g. after navigating back from approval wizard)
  useEffect(() => {
    setRefreshKey(k => k + 1);
  }, []);
  const groupedBills = useMemo(() => {
    const groups: Record<string, typeof filteredBills> = {
      'Due next month': [],
      'Due this month': [],
      Older: []
    };
    filteredBills.forEach(bill => {
      if (bill.dueDate.includes('2026-04')) {
        groups['Due next month'].push(bill);
      } else if (bill.dueDate.includes('2026-03')) {
        groups['Due this month'].push(bill);
      } else {
        groups['Older'].push(bill);
      }
    });
    return Object.entries(groups).filter(([_, bills]) => bills.length > 0);
  }, [filteredBills]);
  const handleSelectAllInGroup = (groupBills: typeof filteredBills) => {
    const selectableBills = groupBills.filter(isBillSelectable);
    const allSelected = selectableBills.every(b => selectedBillIds.has(b.id));
    const newSelected = new Set(selectedBillIds);
    if (allSelected) {
      selectableBills.forEach(b => newSelected.delete(b.id));
    } else {
      selectableBills.forEach(b => newSelected.add(b.id));
    }
    setSelectedBillIds(newSelected);
  };
  const toggleBillSelection = (id: string) => {
    const bill = mockBills.find(b => b.id === id);
    if (bill && !isBillSelectable(bill)) return;
    const newSelected = new Set(selectedBillIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedBillIds(newSelected);
  };
  const selectedBills = mockBills.filter(b => selectedBillIds.has(b.id));
  const totalSelectedAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  const handleSchedulePayments = () => {
    if (selectedBillIds.size === 0) return;
    const idsParam = Array.from(selectedBillIds).join(',');
    navigate(`/bill-approval/schedule?ids=${idsParam}`);
  };
  const handleApproveBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowApproveModal(true);
    setContextMenuOpenId(null);
  };
  const confirmApproveBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => approveBill(id, currentUser));
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowApproveModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleRejectBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowRejectModal(true);
    setContextMenuOpenId(null);
  };
  const confirmRejectBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (rejectAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due (approved status without schedule)
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowRejectModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleCancelScheduled = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowCancelModal(true);
    setContextMenuOpenId(null);
  };
  const confirmCancelScheduled = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (cancelAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowCancelModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleArchiveBill = (id: string) => {
    rejectBill(id, currentUser, 'Archived');
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  const handleReassignBill = (id: string) => {
    // Mock reassignment by clearing the approver so it goes back to general pending pool
    updateBill(id, {
      approver: undefined
    });
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  // Whether the current tab shows approve/reject actions
  const isApprovalTab = activeTab === 'assigned' || activeTab === 'pending';
  // Get summary label based on active tab
  const getSummaryLabel = () => {
    switch (activeTab) {
      case 'assigned':
      case 'pending':
        return 'Bills to review';
      case 'scheduled':
        return 'Scheduled payments';
      default:
        return 'Payments to schedule';
    }
  };
  const summaryLabel = getSummaryLabel();
  const handleSaveApprovals = () => {
    setNumberOfApprovals(tempApprovals);
    setShowApprovalsModal(false);
  };
  const handleSaveThreshold = () => {
    if (!tempThreshold.currency) return;
    setThresholds(prev => [...prev, {
      id: Date.now().toString(),
      approver: tempThreshold.approver,
      amount: tempThreshold.amount,
      currency: tempThreshold.currency
    }]);
    setTempThreshold({
      approver: 'Simon Verraest',
      amount: 0,
      currency: ''
    });
    setShowThresholdModal(false);
  };
  const removeThreshold = (id: string) => {
    setThresholds(prev => prev.filter(t => t.id !== id));
  };
  // Persist cycles to localStorage
  const handleSaveCycles = () => {
    localStorage.setItem('billPaymentCycles', JSON.stringify(tempCycles));
    setPaymentCycles(tempCycles);
    setShowCyclesModal(false);
  };
  const toggleCycleDay = (day: number) => {
    setTempCycles(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      if (prev.length >= 2) return prev; // max 2
      return [...prev, day].sort((a, b) => a - b);
    });
  };
  const formatCycleLabel = (cycles: number[]) => {
    if (cycles.length === 0) return 'No payment cycles';
    return cycles.map(d => {
      const opt = CYCLE_DAY_OPTIONS.find(o => o.value === d);
      return opt ? opt.label : '';
    }).join(' & ');
  };
  return <div className="max-w-[1400px] mx-auto" data-id="element-4205" data-mp="2hmdrv8a0z1gc">
      {/* Header */}
      <div className="flex justify-between items-center mb-8" data-id="element-4206" data-mp="2lpagpgq5rtsq">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4207" data-mp="2qon17utk4os5">
          {t('pages.Bills.bills')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-4208" data-mp="1tymfk4ohiof2">
          <button onClick={() => setShowSettings(true)} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-4209" data-mp="1tzxiy3e0pu5n">
            <SettingsIcon size={18} data-id="element-4210" data-mp="22y98s3728nx1" />
          </button>
          <button onClick={() => navigate('/bill-bulk-upload')} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-4211" data-mp="27phhex0kg595">
            <UploadIcon size={16} data-id="element-4212" data-mp="1f7vpp26gdzel" />
            {t('pages.Bills.upload')}
          </button>
          <button onClick={() => navigate('/bill-form')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4213" data-mp="2ij2q6mphm4ez">
            {t('pages.Bills.add_bill')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start" data-id="element-4214" data-mp="2ncdscajctx0k">
        {/* Main Content */}
        <div className="xl:col-span-3 space-y-6" data-id="element-4215" data-mp="1zm1jmxgabw8x">
          {/* Tabs + Controls */}
          <div className="flex items-end justify-between border-b border-gray-200" data-id="element-4216" data-mp="1unqbcr5n1tst">
            <div className="flex space-x-6" role="tablist" aria-label="Bills" data-id="element-4217" data-mp="28mg6404zjhe0">
              {[{
              id: 'due',
              label: 'Due'
            }, {
              id: 'pending',
              label: 'Pending approval'
            }, {
              id: 'scheduled',
              label: 'Scheduled'
            }, {
              id: 'paid',
              label: 'Paid'
            }, {
              id: 'assigned',
              label: 'Assigned to me'
            }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`bills-panel-${tab.id}`} id={`bills-tab-${tab.id}`} onClick={() => {
              setActiveTab(tab.id as TabType);
              setSelectedBillIds(new Set());
            }} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A] text-[#C41B14]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4218" data-mp="3ik698h3364gn">
                  {tab.label}
                </button>)}
            </div>
            <div className="flex items-center gap-4 pb-3" data-id="element-4219" data-mp="35e1lgll9icu8">
              <div className="relative" data-id="element-4220" data-mp="35bfean2a9zcj">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-4221" data-mp="31bf0n4n0ds6j" />

                <input type="search" aria-label={t('pages.Bills.attrs.search_bills')} placeholder={t('pages.Bills.attrs.search_bills')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4222" data-mp="2weokkhb7ewl4" />
              </div>
              <label className="flex items-center cursor-pointer" data-id="element-4223" data-mp="bn4mqvishsqz">
                <div className="relative" data-id="element-4224" data-mp="bope6c9t4vkm">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-4225" data-mp="6vmju1x7hi3d" />

                  <div className={`block w-9 h-5 rounded-full transition-colors ${showArchived ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-4226" data-mp="j6dxas317xhm" />

                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-4227" data-mp="t5cthb6m20nq" />
                </div>
                <span className="text-xs text-gray-600 ml-2 font-medium" data-id="element-4228" data-mp="1236psm4dkuaw">
                  {t('pages.Bills.show_archived')}
                </span>
              </label>
            </div>
          </div>

          {/* Bills List */}
          <div className="space-y-8" data-id="element-4229" data-mp="1gtw4hyh1orji">
            {groupedBills.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-4230" data-mp="1grt6ar5qns3p">
                <p className="text-gray-500 text-sm" data-id="element-4231" data-mp="1ktep0t4yf0lt">
                  {t('pages.Bills.no_bills_found_matching_your_criteria')}
                </p>
              </div> : groupedBills.map(([groupName, groupBills]) => <div key={groupName} className="space-y-3" data-id="element-4232" data-mp="3qop4j7f73e5r">
                  <div className="flex items-center gap-3" data-id="element-4233" data-mp="3qqa1t4sw9b7b">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-4234" data-mp="3lwxdyww1h8fm">
                      {groupName}
                    </h3>
                    {showCheckboxes && <button onClick={() => handleSelectAllInGroup(groupBills)} className="text-xs font-medium text-[#C41B14] hover:text-[#9F140F]" data-id="element-4235" data-mp="3vobijj2ukyfm">
                        {t('pages.Bills.select')}
                      </button>}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4236" data-mp="2fbcwslgbebeb">
                    <table className="w-full text-left border-collapse" data-id="element-4237" data-mp="2b9rrbl640ze5">
                      <tbody className="divide-y divide-gray-100" data-id="element-4238" data-mp="2149yuivyunqi">
                        {groupBills.map(bill => {
                    const selectable = isBillSelectable(bill);
                    return <tr key={bill.id} className={`hover:bg-gray-50 transition-colors ${selectedBillIds.has(bill.id) ? 'bg-yellow-50/50' : ''}`} data-id="element-4239" data-mp="1rc47nbdjgacd">
                              {showCheckboxes && <td className="px-4 py-4 w-12" data-id="element-4240" data-mp="1mcr9vvl4piv0">
                                  {selectable ? <input type="checkbox" checked={selectedBillIds.has(bill.id)} onChange={() => toggleBillSelection(bill.id)} className="w-4 h-4 text-[#E2231A] border-gray-300 rounded focus:ring-[#E2231A] cursor-pointer" data-id="element-4241" data-mp="1de5w0dqlqs0k" /> : <input type="checkbox" disabled className="w-4 h-4 border-gray-200 rounded opacity-30 cursor-not-allowed" data-id="element-4242" data-mp="ag3x6styzg1q" />}
                                </td>}
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4243" data-mp="3noq8b2q22jh5">
                                <div className="text-sm font-semibold text-gray-900" data-id="element-4244" data-mp="1huqh0sa6riva">
                                  {bill.supplierName}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5" data-id="element-4245" data-mp="34rg83xi9vcsm">
                                  {bill.billNumber}
                                </div>
                              </td>
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4246" data-mp="3dkjlk5sx3pn9">
                                <div className="text-sm font-medium text-gray-900" data-id="element-4247" data-mp="2pup93pcig33j">
                                  {t('pages.BillApprovalWizard.usd')}{' '}
                                  {bill.amount.toLocaleString('en-US', {
                            minimumFractionDigits: 2
                          })}
                                </div>
                              </td>
                              {activeTab === 'paid' ? <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4248" data-mp="giu5m4jdb18e">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-4249" data-mp="3or2ghik3fz0c">
                                      {t('pages.Bills.source')}
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-4250" data-mp="2ib0b56ae99x">
                                      {bill.paymentSource === 'balance' ? 'Balance' : bill.paymentSource === 'card' ? 'Card' : bill.paymentSource === 'bank_transfer' ? 'Bank Transfer' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4251" data-mp="2tnh2h3he5u1e">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-4252" data-mp="2aqy1j1gqrcfx">
                                      {t('pages.Bills.payout')}
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-4253" data-mp="904czoorzkvs">
                                      {bill.payoutType === 'bank_account' ? 'Bank Account' : bill.payoutType === 'promptpay' ? 'PromptPay' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4254" data-mp="1ogpmo62p55bi">
                                    <div className="text-sm text-gray-500" data-id="element-4255" data-mp="279jodybb70xs">
                                      {t('pages.BillDetail.paid_on')}{' '}
                                      {bill.paidDate ? new Date(bill.paidDate).toLocaleDateString('en-GB') : '—'}
                                    </div>
                                  </td>
                                </> : <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4256" data-mp="3o0dvacyrqkp9">
                                    <div className="flex flex-col items-start gap-1" data-id="element-4257" data-mp="2rcftsq863j8z">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4258" data-mp="353kyw7ub13qp">
                                        {bill.status === 'pending_approval' ? 'Pending' : bill.status === 'approved' ? 'Due' : bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                                      </span>
                                      {bill.status === 'pending_approval' && numberOfApprovals > 1 && <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100" data-id="element-4259" data-mp="3507ucjkgvrex">
                                            {t('pages.Bills.requires')}{' '}
                                            {numberOfApprovals}{' '}
                                            {t('pages.Bills.approvals')}
                                          </span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4260" data-mp="1h0cftmdp5znk">
                                    <div className="text-sm text-gray-500" data-id="element-4261" data-mp="12cj5ag3gxnbu">
                                      {t('pages.Bills.due_on')}{' '}
                                      {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                                    </div>
                                  </td>
                                </>}
                              <td className="px-4 py-4 text-right w-12 relative" data-id="element-4262" data-mp="em58kc9xv0ax">
                                {activeTab !== 'paid' && <>
                                    <button onClick={e => {
                            e.stopPropagation();
                            setContextMenuOpenId(contextMenuOpenId === bill.id ? null : bill.id);
                          }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" aria-label="Bill actions" data-id="element-4263" data-mp="2xshnrt37wd2g">
                                      <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-4264" data-mp="2a23vc1gq0c7c" />
                                    </button>

                                    {contextMenuOpenId === bill.id && <>
                                        <div className="fixed inset-0 z-10" onClick={e => {
                              e.stopPropagation();
                              setContextMenuOpenId(null);
                            }} data-id="element-4265" data-mp="1chc2j8mw1fxc" />

                                        <div className="absolute right-8 top-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 animate-in fade-in zoom-in-95 duration-100" data-id="element-4266" data-mp="1vg6ium202ofm">
                                          {activeTab === 'due' && <button onClick={e => {
                                e.stopPropagation();
                                handleArchiveBill(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4267" data-mp="2491q1lb12u81">
                                              <ArchiveIcon size={14} className="mr-2" data-id="element-4268" data-mp="18t2qg16bq56d" />

                                              {t('pages.Bills.archive_bill')}
                                            </button>}

                                          {activeTab === 'pending' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4269" data-mp="uul6mejjhctq">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-4270" data-mp="21my32csghp8r" />

                                                {t('pages.Bills.approve')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4271" data-mp="1nt6sr26wi1bz">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-4272" data-mp="ruommvi7l5y4" />

                                                {t('pages.BillApprovalWizard.cancel')}
                                              </button>
                                            </>}

                                          {activeTab === 'scheduled' && <button onClick={e => {
                                e.stopPropagation();
                                handleCancelScheduled(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4273" data-mp="3cjl3ox1xlckt">
                                              <XCircleIcon size={14} className="mr-2" data-id="element-4274" data-mp="1j2ylqf5f1ifl" />

                                              {t('pages.Bills.cancel_payment')}
                                            </button>}

                                          {activeTab === 'assigned' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4275" data-mp="lghpld8f5avf">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-4276" data-mp="3onjsxawl7297" />

                                                {t('pages.Bills.approve')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleReassignBill(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4277" data-mp="35sldk909wt9g">
                                                <UserPlusIcon size={14} className="mr-2 text-blue-600" data-id="element-4278" data-mp="1lau274apsb82" />

                                                {t('pages.Bills.reassign')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4279" data-mp="12k2ue76dksvs">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-4280" data-mp="2bkuqsfpw2ujf" />

                                                {t('pages.BillApprovalWizard.cancel')}
                                              </button>
                                            </>}
                                        </div>
                                      </>}
                                  </>}
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>)}
          </div>
        </div>

        {/* Right Sidebar (Summary) */}
        <div className="xl:col-span-1" data-id="element-4281" data-mp="3nsft1x6k82dp">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 sticky top-6" data-id="element-4282" data-mp="3srsbm5qeenah">
            <div className="flex justify-between items-start mb-6" data-id="element-4283" data-mp="3e34j68bumtor">
              <div data-id="element-4284" data-mp="3e1tf07ehrjx4">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-4285" data-mp="3e1b90j211rc0">
                  {t('pages.BillApprovalWizard.summary')}
                </h2>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4286" data-mp="353hf1erqj9s0">
                  {t('pages.Bills.based_on_your_selected_bills')}
                </p>
              </div>
              {selectedBillIds.size > 0 && <button onClick={() => setSelectedBillIds(new Set())} className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-4287" data-mp="2v5l6muq6msxl">
                  {t('pages.Bills.clear')}
                </button>}
            </div>
            <div className="space-y-4 mb-8" data-id="element-4288" data-mp="10uv90eslmwfr">
              <div className="flex justify-between items-center text-sm" data-id="element-4289" data-mp="wul1gxp5rxys">
                <span className="text-gray-600" data-id="element-4290" data-mp="wsqcn8arnlpf">
                  {summaryLabel} - {selectedBillIds.size}
                </span>
                {selectedBillIds.size === 0 && <span className="text-gray-400 text-xs" data-id="element-4291" data-mp="1ol7ny9m9htfu">
                    {t('pages.Bills.no_selected_bills')}
                  </span>}
              </div>
              {selectedBillIds.size > 0 && <div className="flex justify-between items-center pt-4 border-t border-gray-200" data-id="element-4292" data-mp="1fn5hvfno8alp">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4293" data-mp="1aolqgzqb66ly">
                    {t('pages.BillDetail.total')}
                  </span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-4294" data-mp="3vbf67l3fgxom">
                    {t('pages.BillApprovalWizard.usd')}{' '}
                    {totalSelectedAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>}
            </div>

            {isApprovalTab ? <div className="flex gap-2" data-id="element-4295" data-mp="3sjzv647nyel">
                <button onClick={() => handleRejectBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-white border border-red-300 text-red-600 hover:bg-red-50 shadow-sm' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'}`} data-id="element-4296" data-mp="3tcw3d5jn5ih">
                  {t('pages.Bills.reject')}
                </button>
                <button onClick={() => handleApproveBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4297" data-mp="2jkqyr8shc4ps">
                  {t('pages.Bills.approve')}
                </button>
              </div> : activeTab === 'scheduled' ? <button onClick={() => handleCancelScheduled()} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4298" data-mp="z1x7ssb1t6ns">
                {t('pages.Bills.cancel_scheduled_payments')}
              </button> : <button onClick={handleSchedulePayments} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4299" data-mp="1vqcxqpi2li1f">
                {t('pages.Bills.schedule_payments')}
              </button>}
          </div>
        </div>
      </div>

      {/* ========== ACTION MODALS ========== */}

      {/* Reject Modal */}
      {showRejectModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-4300" data-mp="2i0bmzyezfk0o" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4301" data-mp="28a06lce43icw">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4302" data-mp="2885xcvc7deon">
              <div className="p-6" data-id="element-4303" data-mp="30yfl7xtbzynp">
                <div className="flex items-center gap-3 mb-4" data-id="element-4304" data-mp="30x4epq8i4csw">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-4305" data-mp="35pnumtunvk0v">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-4306" data-mp="97p0lcm4c9nl" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-4307" data-mp="dav3qbdxpkpu">
                    {t('pages.Bills.reject')}{' '}
                    {actionTargetId ? 'bill' : `${selectedBillIds.size} bills`}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-4308" data-mp="4b8a4brg5j7c">
                  {t('pages.Bills.what_would_you_like_to_do')}{' '}
                  {actionTargetId ? 'bill' : 'bills'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4309" data-mp="i96csjwenr70">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4310" data-mp="i8de867lo7zy">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'archive'} onChange={() => setRejectAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-4311" data-mp="1fok8460iz7ll" />

                    <div className="ml-3" data-id="element-4312" data-mp="397sw2j4a996x">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4313" data-mp="396htunqhk9hx">
                        {t('pages.Bills.archive_bills')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4314" data-mp="2hfbs62v4a3qj">
                        {t('pages.Bills.remove_them_from_the_active_list')}
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'due' ? 'border-[#E2231A] bg-yellow-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4315" data-mp="2qi33m2nzszi6">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'due'} onChange={() => setRejectAction('due')} className="mt-0.5 text-[#E2231A] focus:ring-[#E2231A]" data-id="element-4316" data-mp="rzfy5dv5zqe" />

                    <div className="ml-3" data-id="element-4317" data-mp="33l71gz2tt4yw">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4318" data-mp="33i1afeamzt0j">
                        {t('pages.Bills.move_back_to_due')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4319" data-mp="2bu9e8aj8ackt">
                        {t('pages.Bills.return_them_to_the_due_list')}
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-4320" data-mp="2pkcsae7yezz6">
                  <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4321" data-mp="2plxkhx6sq6tv">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={confirmRejectBills} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4322" data-mp="1klpmevjldety">
                    {t('pages.Bills.confirm_rejection')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Approve Modal */}
      {showApproveModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApproveModal(false)} data-id="element-4323" data-mp="11sviqxrf5951" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4324" data-mp="rxaj369tokyg">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4325" data-mp="nuxewmaq4v3s">
              <div className="p-6 text-center" data-id="element-4326" data-mp="ixf5kussodv9">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-4327" data-mp="2iybxijlmm0r0">
                  <CheckCircleIcon size={24} className="text-green-600" data-id="element-4328" data-mp="2n0gpldwfepbj" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4329" data-mp="298scb7afxusz">
                  {t('pages.Bills.confirm_approval')}
                </h2>
                <p className="text-sm text-gray-600 mb-8" data-id="element-4330" data-mp="2e62bvfrjnmuj">
                  {t('pages.Bills.are_you_sure_you_want_to')}{' '}
                  {actionTargetId ? 'this bill' : `these ${selectedBillIds.size} bills`}
                  ?
                  {numberOfApprovals > 1 && ' This will count as 1 of the required approvals.'}
                </p>
                <div className="flex justify-center gap-3" data-id="element-4331" data-mp="1v9145n6lv8mw">
                  <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4332" data-mp="3knfqy1y2clfr">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={confirmApproveBills} className="px-4 py-2 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#EB4A42] transition-colors" data-id="element-4333" data-mp="3upjfhrjwaftu">
                    {t('pages.Bills.confirm_approval')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Cancel Scheduled Modal */}
      {showCancelModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCancelModal(false)} data-id="element-4334" data-mp="vbpqx0249sui" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4335" data-mp="2x3m0w58myun6">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4336" data-mp="2x5oxx987ho1e">
              <div className="p-6" data-id="element-4337" data-mp="w2fuogv4v17h">
                <div className="flex items-center gap-3 mb-4" data-id="element-4338" data-mp="w3h2mcgur7kz">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-4339" data-mp="10zxv1iv3483v">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-4340" data-mp="15z0v0ow7jo4z" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-4341" data-mp="1azoqbmrgyr50">
                    {t('pages.Bills.cancel_scheduled')}{' '}
                    {actionTargetId ? 'Payment' : 'Payments'}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-4342" data-mp="1jx8rkvpc0nfm">
                  {t('pages.Bills.what_would_you_like_to_do_2')}{' '}
                  {actionTargetId ? 'payment' : 'payments'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4343" data-mp="1yohpds3e1f3u">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'due' ? 'border-[#E2231A] bg-yellow-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4344" data-mp="1ypsnpce2dunk">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'due'} onChange={() => setCancelAction('due')} className="mt-0.5 text-[#E2231A] focus:ring-[#E2231A]" data-id="element-4345" data-mp="3j6jsxl1dllqn" />

                    <div className="ml-3" data-id="element-4346" data-mp="ar8isvnf84v8">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4347" data-mp="apxb4kquucyu">
                        {t('pages.Bills.move_back_to_due')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4348" data-mp="zvozyi2b4mk">
                        {t('pages.Bills.return_them_to_the_due_list_2')}
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4349" data-mp="tij3uw6gtnf7">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'archive'} onChange={() => setCancelAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-4350" data-mp="1u6oh9unqnep" />

                    <div className="ml-3" data-id="element-4351" data-mp="fuhcm54l1nw6">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4352" data-mp="frlhspo17fby">
                        {t('pages.Bills.archive_bills')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4353" data-mp="6s86drgckgol">
                        {t('pages.Bills.remove_them_from_the_active_list')}
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-4354" data-mp="2whsbq6wwyq1t">
                  <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4355" data-mp="2wjd4ps3ky0xk">
                    {t('pages.Bills.keep_scheduled')}
                  </button>
                  <button onClick={confirmCancelScheduled} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4356" data-mp="2t0e2vfyil0ph">
                    {t('pages.Bills.confirm_cancellation')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== BILLS SETTINGS SLIDE-OUT PANEL ========== */}
      {showSettings && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowSettings(false)} data-id="element-4357" data-mp="1mir5zth3t19n" />

          {/* Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200" data-id="element-4358" data-mp="fkoregknxnj6">
            <div className="p-8" data-id="element-4359" data-mp="asxa6wnecu5p">
              {/* Close button */}
              <div className="flex justify-end mb-6" data-id="element-4360" data-mp="5w6rs2o4dmh4">
                <button onClick={() => setShowSettings(false)} aria-label="Close settings" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4361" data-mp="w2bodr4itrf">
                  <XIcon size={18} aria-hidden="true" data-id="element-4362" data-mp="tiwuz7c7fn3b" />
                </button>
              </div>

              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4363" data-mp="onhjun5l05al">
                {t('pages.Bills.bills_settings')}
              </h2>

              {/* Bill payments can be approved by */}
              <div className="mb-8" data-id="element-4364" data-mp="399rgvpl883y6">
                <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-4365" data-mp="359qxrrn6n4ks">
                  {t('pages.Bills.bill_payments_can_be_approved_by')}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed" data-id="element-4366" data-mp="1kqevr8efjnqt">
                  {t('pages.Bills.users_in_these_user_groups_can')}
                </p>
              </div>

              {/* Billing Cycles */}
              <div className="mb-8" data-id="element-4367" data-mp="1yjcun4q2zxz4">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4368" data-mp="1yko6zsxlee1u">
                  {t('pages.Bills.billing_cycles')}
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4369" data-mp="23l262lh7udnq">
                  <span className="text-sm font-medium text-gray-900" data-id="element-4370" data-mp="2ciw2dwezd7aw">
                    {formatCycleLabel(paymentCycles)}
                  </span>
                  <button onClick={() => {
                setTempCycles([...paymentCycles]);
                setShowCyclesModal(true);
              }} aria-label={t('pages.Bills.edit_payment_cycles', {
                defaultValue: 'Edit payment cycles'
              })} className="text-[#C41B14] hover:text-[#9F140F] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-4371" data-mp="2hhphtudtv8dp">
                    <PenIcon size={16} aria-hidden="true" data-id="element-4372" data-mp="1uh8g8sy2zdyn" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-4373" data-mp="29dzf913uennq">
                  {t('pages.Bills.automate_your_payment_runs_by_setting')}
                </p>
              </div>

              {/* Number of Approvals */}
              <div className="mb-8" data-id="element-4374" data-mp="2i59jpngek477">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4375" data-mp="2i8dvb5w7627w">
                  {t('pages.Bills.number_of_approvals')}
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4376" data-mp="2s5sbd4e7kozo">
                  <span className="text-sm font-medium text-gray-900" data-id="element-4377" data-mp="2n77b54yyi5b8">
                    {numberOfApprovals}
                  </span>
                  <button onClick={() => {
                setTempApprovals(numberOfApprovals);
                setShowApprovalsModal(true);
              }} aria-label={t('pages.Bills.edit_number_of_approvals', {
                defaultValue: 'Edit number of approvals'
              })} className="text-[#C41B14] hover:text-[#9F140F] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-4378" data-mp="3bqiqfh6ogjcs">
                    <PenIcon size={16} aria-hidden="true" data-id="element-4379" data-mp="2e437eaylrdn4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-4380" data-mp="2j2d44pzz6mau">
                  {t('pages.Bills.approving_any_payment_for_bills_will')}{' '}
                  {numberOfApprovals}{' '}
                  {t('pages.Bills.users_to_approve_except_when_threshold')}
                </p>
              </div>

              {/* Limits and Thresholds */}
              <div data-id="element-4381" data-mp="1x0ure18x19eh">
                <div className="flex items-center justify-between mb-2" data-id="element-4382" data-mp="1x2fn3u6yuydx">
                  <h3 className="text-xs font-medium text-gray-500" data-id="element-4383" data-mp="1sa5ziktbqaet">
                    {t('pages.Bills.limits_and_thresholds')}
                  </h3>
                  <button onClick={() => {
                setTempThreshold({
                  approver: 'Simon Verraest',
                  amount: 0,
                  currency: ''
                });
                setShowThresholdModal(true);
              }} className="text-xs font-medium text-[#C41B14] hover:text-[#9F140F] transition-colors" data-id="element-4384" data-mp="1nbb84uhajdj3">
                    {t('pages.Bills.add_threshold')}
                  </button>
                </div>

                {thresholds.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4385" data-mp="2yrjaaust8mu8">
                    <span className="text-sm text-gray-500" data-id="element-4386" data-mp="377ibz044ttkz">
                      {t('pages.Bills.no_specific_threshold_approvers_configured_yet')}
                    </span>
                  </div> : <div className="space-y-2" data-id="element-4387" data-mp="3ky3wsud5cdli">
                    {thresholds.map(thr => <div key={thr.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4388" data-mp="2o9o3qoatvm3n">
                        <div data-id="element-4389" data-mp="2x8t502drxtji">
                          <div className="text-sm font-medium text-gray-900" data-id="element-4390" data-mp="2xbnqzycfrmf1">
                            {thr.approver}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-4391" data-mp="ck55i614dgec">
                            {t('pages.Bills.bills_above')} {thr.currency}{' '}
                            {thr.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2
                    })}
                          </div>
                        </div>
                        <button onClick={() => removeThreshold(thr.id)} className="text-[#C41B14] hover:text-[#9F140F] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Remove threshold for ${thr.approver}`} data-id="element-4392" data-mp="1sgv3i0kuieo">
                          <TrashIcon size={14} data-id="element-4393" data-mp="3089r47laz7py" />
                        </button>
                      </div>)}
                  </div>}

                <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-4394" data-mp="3a6pkyibpsv35">
                  {t('pages.Bills.when_threshold_rules_are_configured_payments')}
                </p>
              </div>
            </div>
          </div>
        </>}

      {/* ========== NUMBER OF APPROVALS MODAL ========== */}
      {showApprovalsModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApprovalsModal(false)} data-id="element-4395" data-mp="11t2hwfrik48f" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4396" data-mp="s2hdgbp5s5hl">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4397" data-mp="s04xg42yo576">
              <div className="p-8" data-id="element-4398" data-mp="1fpz7yezt5ho9">
                <div className="flex justify-end mb-4" data-id="element-4399" data-mp="1kromw5q5ifhm">
                  <button onClick={() => setShowApprovalsModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4400" data-mp="1kp2fc639fzze">
                    <XIcon size={18} aria-hidden="true" data-id="element-4401" data-mp="3rjpfb6h52m35" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-4402" data-mp="3vh3oiy4tktv5">
                  {t('pages.Bills.number_of_approvals')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed" data-id="element-4403" data-mp="m20sdlr4wm3c">
                  {t('pages.Bills.the_default_number_of_staff_members')}
                </p>

                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4404" data-mp="d1ly3ww6zwnp">
                  {t('pages.Bills.number_of_approvals')}
                </label>
                <input type="number" min="0" value={tempApprovals} onChange={e => setTempApprovals(parseInt(e.target.value) || 0)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4405" data-mp="19r398rdy2aws" />

                <div className="flex justify-end gap-3 mt-8" data-id="element-4406" data-mp="1op5hhevx7kkd">
                  <button onClick={() => setShowApprovalsModal(false)} className="px-6 py-2.5 text-sm font-medium text-[#C41B14] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4407" data-mp="1onkhvaur1bfn">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveApprovals} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-400 rounded-lg hover:bg-gray-500 transition-colors" data-id="element-4408" data-mp="2a0kujuqe7xrz">
                    {t('pages.BillForm.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD THRESHOLD MODAL ========== */}
      {showThresholdModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowThresholdModal(false)} data-id="element-4409" data-mp="2xq5pzd3qgni5" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4410" data-mp="32p9w1uvd2549">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4411" data-mp="7h9pau6gnm5z">
              <div className="p-8" data-id="element-4412" data-mp="cg2rhqgarqqu">
                <div className="flex justify-end mb-4" data-id="element-4413" data-mp="ch5jk1pcvz8c">
                  <button onClick={() => setShowThresholdModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4414" data-mp="gflcsnc163u7">
                    <XIcon size={18} aria-hidden="true" data-id="element-4415" data-mp="vcvmbr544zna" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4416" data-mp="105x4c80m9fb3">
                  {t('pages.Bills.add_threshold')}
                </h2>

                <div className="space-y-5" data-id="element-4417" data-mp="1481u2vny1rsg">
                  {/* Approver */}
                  <div data-id="element-4418" data-mp="19750ajfnu420">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4419" data-mp="1e4endsfku3mo">
                      {t('pages.Bills.approver_name')}
                    </label>
                    <input type="text" value={tempThreshold.approver} onChange={e => setTempThreshold({
                  ...tempThreshold,
                  approver: e.target.value
                })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4420" data-mp="1o9myfviqrgg8" />
                  </div>

                  {/* Amount */}
                  <div data-id="element-4421" data-mp="1x7gqgu9h3nxl">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4422" data-mp="1x6nkhvic92gi">
                      {t('pages.Bills.this_user_will_need_to_approve')}
                    </label>
                    <input type="number" min="0" value={tempThreshold.amount} onChange={e => setTempThreshold({
                  ...tempThreshold,
                  amount: parseFloat(e.target.value) || 0
                })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4423" data-mp="2prfcm1qj5wm3" />
                  </div>

                  {/* Currency */}
                  <div data-id="element-4424" data-mp="5a6y1s42v07w">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4425" data-mp="5afaxk07a5j3">
                      {t('pages.Bills.select_a_currency_for_this_threshold')}
                    </label>
                    <div className="relative" data-id="element-4426" data-mp="3rhx0ft9hhpcw">
                      <select value={tempThreshold.currency} onChange={e => setTempThreshold({
                    ...tempThreshold,
                    currency: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4427" data-mp="3rioxl8iqbv5a">
                        <option value="" disabled data-id="element-4428" data-mp="3f64ft9prj7m7">
                          {t('pages.Bills.select_a_currency')}
                        </option>
                        <option value="MYR" data-id="element-4429" data-mp="3swgbtjpgxqim">{t('pages.Bills.myr')}</option>
                        <option value="MYR" data-id="element-4430" data-mp="3ove9gu098ycf">MYR</option>
                        <option value="EUR" data-id="element-4431" data-mp="6qt2yckfbcn3">{t('pages.Bills.eur')}</option>
                        <option value="GBP" data-id="element-4432" data-mp="6q1gbr8t0p8y">{t('pages.Bills.gbp')}</option>
                        <option value="SGD" data-id="element-4433" data-mp="1t1bfjnoxsjf">{t('pages.Bills.sgd')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4434" data-mp="23jwb4ml0qbvf">
                  <button onClick={() => setShowThresholdModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4435" data-mp="250ieh2lpug8i">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveThreshold} disabled={!tempThreshold.currency} className="px-6 py-2.5 text-sm font-medium text-[#C41B14] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4436" data-mp="2jwrei6dfkxlz">
                    {t('pages.Bills.add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD CYCLES MODAL ========== */}
      {showCyclesModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCyclesModal(false)} data-id="element-4437" data-mp="18hkneaixgask" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4438" data-mp="3lruc5yd2jm0i">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4439" data-mp="3hrjx7w7atl9i">
              <div className="p-8" data-id="element-4440" data-mp="3cqe3bv1wjrye">
                <div className="flex justify-end mb-4" data-id="element-4441" data-mp="3cts4izytxv49">
                  <button onClick={() => setShowCyclesModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4442" data-mp="37s2u9mjfllh8">
                    <XIcon size={18} aria-hidden="true" data-id="element-4443" data-mp="2u296z8nkr99m" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4444" data-mp="2p3xvyuljmspt">
                  {t('pages.Bills.payment_cycles')}
                </h2>

                <div className="space-y-4" data-id="element-4445" data-mp="2f46xkhtv3tkg">
                  {CYCLE_DAY_OPTIONS.map(opt => <label key={opt.value} className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-4446" data-mp="25gkx9jswsse4">
                      <input type="checkbox" checked={tempCycles.includes(opt.value)} onChange={() => toggleCycleDay(opt.value)} disabled={!tempCycles.includes(opt.value) && tempCycles.length >= 2} className="w-4 h-4 text-[#E2231A] border-gray-300 rounded focus:ring-[#E2231A] cursor-pointer" data-id="element-4447" data-mp="1wg69mdsh317g" />

                      <span className="ml-3 text-sm text-gray-700" data-id="element-4448" data-mp="2jfkjkksy8v74">
                        {opt.label}
                      </span>
                    </label>)}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4449" data-mp="3bwww7n3k2ear">
                  <button onClick={() => setShowCyclesModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4450" data-mp="10h64rfsxgjur">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveCycles} className="px-6 py-2.5 text-sm font-medium text-[#C41B14] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4451" data-mp="1596axpzlc8zs">
                    {t('pages.Bills.save_cycles')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}